import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Container, Typography, Grid, FormControl, InputLabel, Select, Switch,
  MenuItem, CircularProgress, Paper, Alert, Tooltip as MuiTooltip, IconButton, Card,
  CardContent, FormGroup, FormControlLabel, Checkbox, Skeleton, LinearProgress, Button, Stack,
  Accordion, AccordionSummary, AccordionDetails, useMediaQuery, useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimelineIcon from '@mui/icons-material/Timeline';

const RANK_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F',
  '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57', '#ff7300', '#413ea0'
];

const calculateLinearRegression = (data) => {
  if (!data || data.length < 2) {
    return { slope: null, intercept: null, rSquared: null };
  }
  const n = data.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  const points = [];

  data.forEach(point => {
    const x = point.year;
    const y = Object.keys(point).reduce((total, key) => key !== 'year' ? total + point[key] : total, 0);
    points.push({ x, y });

    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const numerator = n * sumXY - sumX * sumY;
  const denominator = n * sumX2 - sumX * sumX;

  if (denominator === 0) {
    return { slope: null, intercept: null, rSquared: null };
  }

  const slope = numerator / denominator;
  const intercept = (sumY / n) - slope * (sumX / n);
  const meanY = sumY / n;

  let ssTot = 0;
  let ssRes = 0;

  points.forEach(point => {
    const predictedY = slope * point.x + intercept;
    ssTot += Math.pow(point.y - meanY, 2);
    ssRes += Math.pow(point.y - predictedY, 2);
  });

  const rSquared = ssTot === 0 ? 1 : 1 - (ssRes / ssTot);

  return { slope, intercept, rSquared };
};

const CumulativeDescriptionTrends = () => {
  const { t } = useTranslation(['analysis', 'common']);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [rankFilter, setRankFilter] = useState('All');
  const [subfamilyFilter, setSubfamilyFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const [rankOptions, setRankOptions] = useState([]);
  const [subfamilyOptions, setSubfamilyOptions] = useState([]);
  const [fullYearRange, setFullYearRange] = useState([0, 0]);
  const [startYear, setStartYear] = useState(0);
  const [endYear, setEndYear] = useState(0);

  const [resetCumulative, setResetCumulative] = useState(false);
  const [showLogistic, setShowLogistic] = useState(false);
  const [showGompertz, setShowGompertz] = useState(false);
  const [showLinearTrend, setShowLinearTrend] = useState(true);
  const [curveLoading, setCurveLoading] = useState(false);
  const [curveFitResults, setCurveFitResults] = useState(null);
  const [showLogisticBand, setShowLogisticBand] = useState(true);
  const [showGompertzBand, setShowGompertzBand] = useState(true);

  const chartRef = useRef(null);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  // CSV download helper
  const downloadCSV = (data, filename = 'cumulative_trends.csv') => {
    if (!data || data.length === 0) return;
    const keys = Array.from(new Set(data.flatMap(d => Object.keys(d))));
    const header = keys.join(',');
    const rows = data.map(d => keys.map(k => d[k] ?? '').join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Chart export helpers (SVG and PNG)
  const exportChartSVG = (filename = 'chart.svg') => {
    if (!chartRef.current) return;
    const svg = chartRef.current.querySelector('svg');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportChartPNG = async (filename = 'chart.png') => {
    if (!chartRef.current) return;
    const svg = chartRef.current.querySelector('svg');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const img = new Image();
    const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.width || 1200;
    canvas.height = img.height || 600;
    const ctx2 = canvas.getContext('2d');
    ctx2.fillStyle = window.getComputedStyle(document.body).backgroundColor || '#ffffff';
    ctx2.fillRect(0, 0, canvas.width, canvas.height);
    ctx2.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    }, 'image/png');
  };
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allData = [];
      const { default: supabase } = await import('../../../utils/supabase');
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error: rpcError } = await supabase.rpc('get_yearly_taxa_stats', { p_limit: pageSize, p_offset: page * pageSize });
        if (rpcError) throw rpcError;
        if (data && data.length > 0) {
          allData.push(...data);
          if (data.length < pageSize) hasMore = false;
          else page++;
        } else {
          hasMore = false;
        }
      }
      setRawData(allData);
      const ranks = [...new Set(allData.map(item => item.rank))].sort();
      const subfamilies = [...new Set(allData.map(item => item.subfamily_name))].filter(Boolean).sort();
      setRankOptions(['All', ...ranks]);
      setSubfamilyOptions(['All', ...subfamilies]);
    } catch (e) {
      console.error("Failed to fetch taxa trends:", e);
      setError(t('error.fetchMessage'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const titleSx = {
    fontWeight: 800,
    letterSpacing: "0.01em",
    color: "text.primary",
    textAlign: "center",
    mb: 1,
  };

  const subtitleSx = {
    textAlign: "center",
    whiteSpace: "pre-line",
    color: "text.secondary",
    maxWidth: 820,
    mx: "auto",
    mb: 4,
  };

  useEffect(() => {
    if (rawData.length > 0 && fullYearRange[0] === 0) {
      const years = rawData.map(item => item.authority_year).filter(Boolean);
      if (years.length > 0) {
        const min = Math.min(...years);
        const max = Math.max(...years);
        setFullYearRange([min, max]);
        setStartYear(min);
        setEndYear(max);
      }
    }
  }, [rawData, fullYearRange]);

  const yearOptions = useMemo(() => {
    if (fullYearRange[0] === 0) return [];
    const options = [];
    for (let y = fullYearRange[0]; y <= fullYearRange[1]; y++) {
      options.push(y);
    }
    return options;
  }, [fullYearRange]);

  const handleStartYearChange = (event) => {
    const newStartYear = event.target.value;
    setStartYear(newStartYear);
    if (newStartYear > endYear) setEndYear(newStartYear);
  };

  const handleEndYearChange = (event) => {
    const newEndYear = event.target.value;
    setEndYear(newEndYear);
    if (newEndYear < startYear) setStartYear(newEndYear);
  };

  const { baseChartData, allRanks, totalCount } = useMemo(() => {
    const filteredRawData = rawData.filter(item => {
        const rankMatch = rankFilter === 'All' || item.rank === rankFilter;
        const subfamilyMatch = subfamilyFilter === 'All' || item.subfamily_name === subfamilyFilter;
        const statusMatch = statusFilter === 'All' || (statusFilter === 'Valid' && item.status === 'valid') || (statusFilter === 'Invalid' && item.status !== 'valid');
        return rankMatch && subfamilyMatch && statusMatch;
    });

    const dataInPeriod = filteredRawData.filter(item => item.authority_year >= startYear && item.authority_year <= endYear);
    
    if (dataInPeriod.length === 0 || startYear === 0) {
      return { baseChartData: [], allRanks: [], totalCount: 0 };
    }
    
    const totalCount = dataInPeriod.reduce((acc, item) => acc + item.count, 0);
    const ranks = [...new Set(dataInPeriod.map(item => item.rank))].sort();

    const yearlyCountsByRank = dataInPeriod.reduce((acc, item) => {
      const { authority_year: year, rank, count } = item;
      if (year) {
        if (!acc[year]) acc[year] = {};
        acc[year][rank] = (acc[year][rank] || 0) + count;
      }
      return acc;
    }, {});

    const chartData = [];
    let cumulativeCountsByRank = {};
    ranks.forEach(rank => { cumulativeCountsByRank[rank] = 0; });

    if (!resetCumulative) {
      filteredRawData.filter(item => item.authority_year < startYear).forEach(item => {
        if (ranks.includes(item.rank)) {
          cumulativeCountsByRank[item.rank] = (cumulativeCountsByRank[item.rank] || 0) + item.count;
        }
      });
    }

    for (let year = startYear; year <= endYear; year++) {
      const cumulativeYearData = { year };
      const countsForYear = yearlyCountsByRank[year] || {};
      ranks.forEach(rank => {
        const count = countsForYear[rank] || 0;
        cumulativeCountsByRank[rank] += count;
        cumulativeYearData[rank] = cumulativeCountsByRank[rank];
      });
      chartData.push(cumulativeYearData);
    }

    return { baseChartData: chartData, allRanks: ranks, totalCount };
  }, [rawData, rankFilter, subfamilyFilter, statusFilter, startYear, endYear, resetCumulative]);

  useEffect(() => {
    const fetchCurveFits = async () => {
      if (!baseChartData || baseChartData.length < 3) {
        setCurveFitResults(null);
        return;
      }
      setCurveLoading(true);
      try {
        const pointsForFit = baseChartData.map(d => ({
          year: d.year,
          total: Object.keys(d).reduce((sum, key) => (key !== 'year') ? sum + d[key] : sum, 0)
        }));

        const { default: supabase } = await import('../../../utils/supabase');
        const { data, error } = await supabase.functions.invoke('fit-curves', {
          body: { data: pointsForFit },
        });

        if (error) throw error;
        setCurveFitResults(data);
      } catch (e) {
        console.error("Failed to fetch curve fits:", e);
        setCurveFitResults(null);
      } finally {
        setCurveLoading(false);
      }
    };
    fetchCurveFits();
  }, [baseChartData]);

  const { chartData, linearTrendStats } = useMemo(() => {
    if (!baseChartData || baseChartData.length === 0) {
      return { chartData: [], linearTrendStats: { slope: null, intercept: null, rSquared: null } };
    }

    const linearTrendStats = calculateLinearRegression(baseChartData);
    
    const finalChartData = baseChartData.map(d => {
      const newPoint = { ...d };
      if (linearTrendStats.slope !== null) {
        newPoint.linearTrend = Math.max(0, linearTrendStats.slope * d.year + linearTrendStats.intercept);
      }
      
      return newPoint;
    });

    if (curveFitResults) {
      const logisticFit = curveFitResults.find(f => f.model_name === 'logistic');
      if (logisticFit?.params) {
        finalChartData.forEach(d => {
          d.logisticTrend = logisticFit.params.L / (1 + Math.exp(-logisticFit.params.k * (d.year - logisticFit.params.x0)));
        });
      }

      const gompertzFit = curveFitResults.find(f => f.model_name === 'gompertz');
      if (gompertzFit?.params) {
        finalChartData.forEach(d => {
          d.gompertzTrend = gompertzFit.params.a_gompertz * Math.exp(-gompertzFit.params.b_gompertz * Math.exp(-gompertzFit.params.c_gompertz * d.year));
        });
      }

      // If curve fit results include per-year curveBand data (from bootstrap), build bands
      if (logisticFit?.ci && logisticFit.ci.curveYears && logisticFit.ci.curveBand) {
        const logisticBandMap = new Map(logisticFit.ci.curveYears.map((yr, idx) => [yr, logisticFit.ci.curveBand[idx]]));
        finalChartData.forEach(d => {
          const band = logisticBandMap.get(d.year);
          if (band && Array.isArray(band) && band.length === 2) {
            d.logistic_band_base = band[0];
            d.logistic_band_diff = Math.max(0, band[1] - band[0]);
          } else {
            d.logistic_band_base = 0;
            d.logistic_band_diff = 0;
          }
        });
      }

      if (gompertzFit?.ci && gompertzFit.ci.curveYears && gompertzFit.ci.curveBand) {
        const gompertzBandMap = new Map(gompertzFit.ci.curveYears.map((yr, idx) => [yr, gompertzFit.ci.curveBand[idx]]));
        finalChartData.forEach(d => {
          const band = gompertzBandMap.get(d.year);
          if (band && Array.isArray(band) && band.length === 2) {
            d.gompertz_band_base = band[0];
            d.gompertz_band_diff = Math.max(0, band[1] - band[0]);
          } else {
            d.gompertz_band_base = 0;
            d.gompertz_band_diff = 0;
          }
        });
      }
    }

    return { chartData: finalChartData, linearTrendStats };
  }, [baseChartData, curveFitResults]);

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Container maxWidth="xl">
        <Typography variant="h2" sx={titleSx}>
          {t('cumulativeTrends.title')}
        </Typography>
        <Typography variant="body1" sx={subtitleSx}>
          {t('cumulativeTrends.description')}
        </Typography>

        {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />}
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            <Typography variant="h6">{t('error.title')}</Typography>
            <Typography>{error}</Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>{t('error.adminHintTitle')}</strong> {t('error.adminHintBody')}
            </Typography>
          </Alert>
        )}

        {!loading && !error && (
          <>
            {isSm ? (
              <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('parametersTitle')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:subfamily')}</InputLabel><Select value={subfamilyFilter} label={t('common:subfamily')} onChange={(e) => setSubfamilyFilter(e.target.value)}>{subfamilyOptions.map(sf => <MenuItem key={sf} value={sf}>{sf}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:rank')}</InputLabel><Select value={rankFilter} label={t('common:rank')} onChange={(e) => setRankFilter(e.target.value)}>{rankOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:status')}</InputLabel><Select value={statusFilter} label={t('common:status')} onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value="All">{t('common:all')}</MenuItem><MenuItem value="Valid">{t('common:valid')}</MenuItem><MenuItem value="Invalid">{t('common:invalid')}</MenuItem></Select></FormControl></Grid>
                      <Grid item xs={6} sm={3} md={1.5}><FormControl fullWidth><InputLabel>{t('startYear')}</InputLabel><Select value={startYear} label={t('startYear')} onChange={handleStartYearChange} disabled={yearOptions.length === 0}>{yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={6} sm={3} md={1.5}><FormControl fullWidth><InputLabel>{t('endYear')}</InputLabel><Select value={endYear} label={t('endYear')} onChange={handleEndYearChange} disabled={yearOptions.length === 0}>{yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
                    </Grid>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('parametersTitle')}</Typography>
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:subfamily')}</InputLabel><Select value={subfamilyFilter} label={t('common:subfamily')} onChange={(e) => setSubfamilyFilter(e.target.value)}>{subfamilyOptions.map(sf => <MenuItem key={sf} value={sf}>{sf}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:rank')}</InputLabel><Select value={rankFilter} label={t('common:rank')} onChange={(e) => setRankFilter(e.target.value)}>{rankOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:status')}</InputLabel><Select value={statusFilter} label={t('common:status')} onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value="All">{t('common:all')}</MenuItem><MenuItem value="Valid">{t('common:valid')}</MenuItem><MenuItem value="Invalid">{t('common:invalid')}</MenuItem></Select></FormControl></Grid>
                  <Grid item xs={6} sm={3} md={1.5}><FormControl fullWidth><InputLabel>{t('startYear')}</InputLabel><Select value={startYear} label={t('startYear')} onChange={handleStartYearChange} disabled={yearOptions.length === 0}>{yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item xs={6} sm={3} md={1.5}><FormControl fullWidth><InputLabel>{t('endYear')}</InputLabel><Select value={endYear} label={t('endYear')} onChange={handleEndYearChange} disabled={yearOptions.length === 0}>{yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
                </Grid>
              </Paper>
            )}

            <Paper elevation={3} sx={{ p: { xs: 1, sm: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('cumulativeTrends.chartTitle')}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormGroup row>
                    <FormControlLabel control={<Switch checked={resetCumulative} onChange={(e) => setResetCumulative(e.target.checked)} />} label={t('cumulativeTrends.resetCumulative')} />
                    <FormControlLabel control={<Checkbox checked={showLinearTrend} onChange={(e) => setShowLinearTrend(e.target.checked)} />} label={t('cumulativeTrends.showLinearTrend')} />
                    <FormControlLabel control={<Checkbox checked={showLogistic} onChange={(e) => setShowLogistic(e.target.checked)} disabled={!curveFitResults || !curveFitResults.find(f => f.model_name === 'logistic')?.params} />} label={t('cumulativeTrends.showLogistic')} />
                    <FormControlLabel control={<Checkbox checked={showGompertz} onChange={(e) => setShowGompertz(e.target.checked)} disabled={!curveFitResults || !curveFitResults.find(f => f.model_name === 'gompertz')?.params} />} label={t('cumulativeTrends.showGompertz')} />
                    <FormControlLabel control={<Checkbox checked={showLogisticBand} onChange={(e) => setShowLogisticBand(e.target.checked)} disabled={!curveFitResults || !curveFitResults.find(f => f.model_name === 'logistic')?.ci} />} label={t('cumulativeTrends.showLogisticBand')} />
                    <FormControlLabel control={<Checkbox checked={showGompertzBand} onChange={(e) => setShowGompertzBand(e.target.checked)} disabled={!curveFitResults || !curveFitResults.find(f => f.model_name === 'gompertz')?.ci} />} label={t('cumulativeTrends.showGompertzBand')} />
                  </FormGroup>
                  <Button size="small" variant="outlined" onClick={() => downloadCSV(chartData)}>
                    {t('cumulativeTrends.downloadCSV')}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => exportChartPNG()}>
                    {t('cumulativeTrends.downloadPNG')}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => exportChartSVG()}>
                    {t('cumulativeTrends.downloadSVG')}
                  </Button>
                </Stack>
              </Box>
              {curveLoading && <LinearProgress sx={{ my: 1 }} /> }
              {loading ? (
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
              ) : (chartData.length > 0 ? (
                <div ref={chartRef}>
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis label={{ value: t('cumulativeCount'), angle: -90, position: 'insideLeft' }} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(30, 30, 30, 0.95)', border: '1px solid rgba(127, 255, 212, 0.3)', borderRadius: '8px' }} />
                    <Legend />
                    {allRanks.map((rank, index) => (
                      <Area key={rank} type="monotone" dataKey={rank} stackId="1" stroke={RANK_COLORS[index % RANK_COLORS.length]} fill={RANK_COLORS[index % RANK_COLORS.length]} name={rank} />
                    ))}

                    {/* CI bands (if available) */}
                    {showLogisticBand && curveFitResults && curveFitResults.find(f => f.model_name === 'logistic')?.ci && (
                      <>
                        <Area dataKey="logistic_band_base" stackId="band_logistic" stroke="none" fill="transparent" isAnimationActive={false} />
                        <Area dataKey="logistic_band_diff" stackId="band_logistic" stroke="none" fill="#00C49F" fillOpacity={0.12} isAnimationActive={false} />
                      </>
                    )}
                    {showGompertzBand && curveFitResults && curveFitResults.find(f => f.model_name === 'gompertz')?.ci && (
                      <>
                        <Area dataKey="gompertz_band_base" stackId="band_gompertz" stroke="none" fill="transparent" isAnimationActive={false} />
                        <Area dataKey="gompertz_band_diff" stackId="band_gompertz" stroke="none" fill="#ff7300" fillOpacity={0.12} isAnimationActive={false} />
                      </>
                    )}

                    {/* trend lines */}
                    {showLinearTrend && linearTrendStats.slope !== null && (
                      <Line type="monotone" dataKey="linearTrend" stroke="#ffffff" strokeWidth={2} dot={false} />
                    )}
                    {showLogistic && curveFitResults && curveFitResults.find(f => f.model_name === 'logistic')?.params && (
                      <Line type="monotone" dataKey="logisticTrend" stroke="#00C49F" strokeWidth={2} dot={false} />
                    )}
                    {showGompertz && curveFitResults && curveFitResults.find(f => f.model_name === 'gompertz')?.params && (
                      <Line type="monotone" dataKey="gompertzTrend" stroke="#ff7300" strokeWidth={2} dot={false} />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
                </div>
              ) : (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'transparent' }}>
                  <Typography color="text.secondary">{t('noData')}</Typography>
                </Paper>
              ))}
            </Paper>

            {chartData.length > 0 && (
              <Box>
                <Box mb={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('resultsTitle')}</Typography>
                  </Box>
                  <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', display: 'inline-block', minWidth: 275 }}>
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography color="text.secondary" gutterBottom>{t('taxaNumberTitle')}</Typography>
                      <Typography variant="h4" component="div">{totalCount.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Box>

                {(linearTrendStats.slope !== null || (curveFitResults && curveFitResults.some(f => f.r_squared !== null))) && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AssessmentIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">{t('linearRegressionTitle')}</Typography>
                    </Box>
                    <Grid container spacing={3}>
                      {/* 線形回帰の統計 */}
                      {linearTrendStats.slope !== null && linearTrendStats.rSquared !== null && (
                        <>
                          <Grid item xs={12} sm={6} md={4}>
                            <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', height: '100%' }}>
                              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <Typography color="text.secondary" gutterBottom>{t('trendSlope')} ({t('cumulativeTrends.linearCurveName')})</Typography>
                                  <MuiTooltip title={t('stats.trendTooltip')}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></MuiTooltip>
                                </Box>
                                <Typography variant="h4" component="div">{linearTrendStats.slope.toFixed(2)}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}> {/* R2値は別のカードとして表示 */}
                            <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', height: '100%' }}>
                              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <Typography color="text.secondary" gutterBottom>{t('rSquared')} ({t('cumulativeTrends.linearCurveName')})</Typography>
                                  <MuiTooltip title={t('stats.rSquaredTooltip')}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></MuiTooltip>
                                </Box>
                                <Typography variant="h4" component="div">{linearTrendStats.rSquared.toFixed(3)}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </>
                      )}

                      {/* 非線形回帰の統計 (サーバーからの結果を使用) */}
                      {curveFitResults && curveFitResults.map((fit, index) => {
                        if (fit.r_squared === null) return null;
                        let modelNameKey = '';
                        switch (fit.model_name) {
                          case 'logistic':
                            modelNameKey = 'cumulativeTrends.logisticCurveName';
                            break;
                          case 'gompertz':
                            modelNameKey = 'cumulativeTrends.gompertzCurveName';
                            break;
                          case 'exponential':
                            modelNameKey = 'cumulativeTrends.exponentialCurveName';
                            break;
                          default:
                            return null;
                        }
                        return (
                          <Grid item xs={12} sm={6} md={4} key={fit.model_name}>
                            <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', height: '100%' }}>
                              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <Typography color="text.secondary" gutterBottom>{t('rSquared')} ({t(modelNameKey)})</Typography>
                                  <MuiTooltip title={t('stats.rSquaredTooltip')}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></MuiTooltip>
                                </Box>
                                <Typography variant="h4" component="div">{fit.r_squared.toFixed(3)}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default CumulativeDescriptionTrends;
